import { useEffect, useMemo, useState } from "react";

const PRESET_OPTIONS = [15, 30, 60];

export default function ExtraTimeRequestModal({
  open,
  onClose,
  onSubmit,
  submitting = false,
  error = "",
  plannedWindowLabel = "",
}) {
  const [selectedMinutes, setSelectedMinutes] = useState(30);
  const [customMinutes, setCustomMinutes] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedMinutes(30);
    setCustomMinutes("");
    setReason("");
  }, [open]);

  const resolvedMinutes = useMemo(() => {
    if (selectedMinutes === "custom") {
      const parsed = Number(customMinutes);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return Number(selectedMinutes || 0);
  }, [customMinutes, selectedMinutes]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Extra tijd aanvragen</h3>
            <p className="mt-1 text-sm text-gray-600">
              Stuur een verzoek naar de planner. De planning wordt pas aangepast na goedkeuring.
            </p>
            {plannedWindowLabel ? (
              <p className="mt-2 text-xs text-gray-500">Geplande tijd: {plannedWindowLabel}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            Sluiten
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-800">Extra tijd</p>
            <div className="flex flex-wrap gap-2">
              {PRESET_OPTIONS.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => setSelectedMinutes(minutes)}
                  className={`rounded-full border px-3 py-2 text-sm ${
                    selectedMinutes === minutes
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  +{minutes} min
                </button>
              ))}
              <button
                type="button"
                onClick={() => setSelectedMinutes("custom")}
                className={`rounded-full border px-3 py-2 text-sm ${
                  selectedMinutes === "custom"
                    ? "border-blue-600 bg-blue-50 text-blue-700"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Aangepast
              </button>
            </div>
            {selectedMinutes === "custom" && (
              <input
                type="number"
                min="1"
                step="5"
                value={customMinutes}
                onChange={(event) => setCustomMinutes(event.target.value)}
                placeholder="Bijv. 45"
                className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-800">
              Reden
            </label>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={4}
              placeholder="Bijvoorbeeld: extra melders vervangen of installatie complexer dan verwacht."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            disabled={submitting || !resolvedMinutes}
            onClick={() =>
              onSubmit({
                requestedMinutes: resolvedMinutes,
                reason,
              })
            }
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Versturen..." : "Verzoek versturen"}
          </button>
        </div>
      </div>
    </div>
  );
}
